import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';

@Component({
  selector: 'app-combobox',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ClickOutsideDirective],
  templateUrl: './combobox.component.html',
  styleUrl: './combobox.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ComboboxComponent),
      multi: true,
    },
  ],
})
export class ComboboxComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);

  @Input() id = '';
  @Input() name = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() options: any[] = [];
  @Input() labelKey = 'name';
  @Input() valueKey = 'name';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() labelFormatter: ((item: any) => string) | null = null;
  @Input() placeholder = '';
  @Input() searchPlaceholder = '';
  @Input() emptyText = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() clearable = true;
  @Input() autoSort = false;
  @Input() customClass = '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() selectionChange = new EventEmitter<any>();

  @ViewChild('searchInput') searchInputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('optionsList') optionsListRef?: ElementRef<HTMLDivElement>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any = '';
  isOpen = false;
  searchQuery = '';
  highlightedIndex = -1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredOptions: any[] = [];

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-explicit-any
  private onChange: (value: any) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.filterOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.filterOptions();
      // If current value no longer exists in new options, we don't necessarily clear it,
      // but if the list changed, ensure highlight index is fresh
      this.highlightedIndex = this.findSelectedIndex();
    }
  }

  // ControlValueAccessor methods
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(value: any): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled && this.isOpen) {
      this.closeDropdown();
    }
    this.cdr.markForCheck();
  }

  // Helpers for labels and values
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionLabel(option: any): string {
    if (option === null || option === undefined) return '';
    if (this.labelFormatter) {
      return this.labelFormatter(option);
    }
    if (typeof option === 'object') {
      return (
        option[this.labelKey] ??
        option.label ??
        option.name ??
        option.code ??
        option.value ??
        ''
      );
    }
    return String(option);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionValue(option: any): any {
    if (option === null || option === undefined) return '';
    if (typeof option === 'object') {
      return (
        option[this.valueKey] ??
        option.value ??
        option.name ??
        option.code ??
        option.id ??
        option
      );
    }
    return option;
  }

  get selectedLabel(): string {
    if (this.value === '' || this.value === null || this.value === undefined) {
      return this.placeholder;
    }
    const found = (this.options || []).find(
      (opt) => this.getOptionValue(opt) === this.value,
    );
    if (found) {
      return this.getOptionLabel(found);
    }
    return String(this.value);
  }

  get hasSelectedValue(): boolean {
    return this.value !== '' && this.value !== null && this.value !== undefined;
  }

  get effectiveSearchPlaceholder(): string {
    if (this.searchPlaceholder) return this.searchPlaceholder;
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SEARCH_PLACEHOLDER',
    );
  }

  get effectiveEmptyText(): string {
    if (this.emptyText) return this.emptyText;
    if (this.searchQuery) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.NO_RESULTS',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.NO_OPTIONS',
    );
  }

  filterOptions(): void {
    const list = this.options || [];
    const query = (this.searchQuery || '').trim().toLowerCase();
    if (!query) {
      this.filteredOptions = [...list];
    } else {
      this.filteredOptions = list.filter((item) => {
        const label = this.getOptionLabel(item).toLowerCase();
        const val = String(this.getOptionValue(item)).toLowerCase();
        return label.includes(query) || val.includes(query);
      });
    }
    if (this.autoSort) {
      this.filteredOptions.sort((a, b) =>
        this.getOptionLabel(a).localeCompare(
          this.getOptionLabel(b),
          undefined,
          { sensitivity: 'base' },
        ),
      );
    }
    this.highlightedIndex = -1;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.filterOptions();
  }

  clearSearch(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.searchQuery = '';
    this.filterOptions();
    this.searchInputElement?.nativeElement.focus();
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown(): void {
    if (this.disabled || this.isOpen) return;
    this.isOpen = true;
    this.searchQuery = '';
    this.filterOptions();
    this.highlightedIndex = this.findSelectedIndex();
    this.onTouched();

    setTimeout(() => {
      this.searchInputElement?.nativeElement.focus();
      this.scrollHighlightedIntoView();
    }, 50);
  }

  closeDropdown(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.searchQuery = '';
    this.highlightedIndex = -1;
    this.cdr.markForCheck();
  }

  onClickOutside(): void {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectOption(option: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const val = this.getOptionValue(option);
    this.value = val;
    this.onChange(val);
    this.selectionChange.emit(val);
    this.closeDropdown();
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.value = '';
    this.onChange('');
    this.selectionChange.emit('');
    if (this.isOpen) {
      this.filterOptions();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isSelected(option: any): boolean {
    return this.getOptionValue(option) === this.value;
  }

  private findSelectedIndex(): number {
    if (!this.hasSelectedValue) return -1;
    return this.filteredOptions.findIndex((opt) => this.isSelected(opt));
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
        event.preventDefault();
        this.openDropdown();
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.closeDropdown();
        }
        break;
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveHighlight(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (
          this.highlightedIndex >= 0 &&
          this.highlightedIndex < this.filteredOptions.length
        ) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  private moveHighlight(step: number): void {
    if (this.filteredOptions.length === 0) return;
    let nextIndex = this.highlightedIndex + step;
    if (nextIndex < 0) {
      nextIndex = this.filteredOptions.length - 1;
    } else if (nextIndex >= this.filteredOptions.length) {
      nextIndex = 0;
    }
    this.highlightedIndex = nextIndex;
    this.scrollHighlightedIntoView();
  }

  private scrollHighlightedIntoView(): void {
    if (!this.optionsListRef || this.highlightedIndex < 0) return;
    const container = this.optionsListRef.nativeElement;
    const items = container.querySelectorAll('.combobox-option-item');
    const targetItem = items[this.highlightedIndex] as HTMLElement;
    if (targetItem) {
      const containerTop = container.scrollTop;
      const containerBottom = containerTop + container.clientHeight;
      const itemTop = targetItem.offsetTop;
      const itemBottom = itemTop + targetItem.offsetHeight;

      if (itemTop < containerTop) {
        container.scrollTop = itemTop;
      } else if (itemBottom > containerBottom) {
        container.scrollTop = itemBottom - container.clientHeight;
      }
    }
  }
}
